import React from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "../context/LanguageContext";
import SEO from "../components/SEO";
import mandiImg from "../assets/images/authentic/bikaner_wool_mandi.webp";
import balesStorageImg from "../assets/images/authentic/warehouse_bales_storage.webp";
import {
  MapPin,
  Award,
  Sliders,
  PackageCheck,
  CheckCircle2,
  ArrowRight,
  Phone,
  MessageCircle,
  Building2,
  Scale,
} from "lucide-react";

const WoolSupplierBikaner = () => {
  const { lPath, isRTL } = useLanguage();

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://skwoolens.trivanahospitality.com/en"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Wool Supplier in Bikaner",
        "item": "https://skwoolens.trivanahospitality.com/en/wool-supplier-bikaner"
      }
    ]
  };

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Raw Wool Supply in Bikaner",
    "provider": {
      "@type": "WholesaleStore",
      "name": "S.K. Woollens",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "B-26, Un Mandi, Antyodaya Nagar",
        "addressLocality": "Bikaner",
        "addressRegion": "Rajasthan",
        "postalCode": "334001",
        "addressCountry": "IN"
      },
      "telephone": "+91-7014581250"
    },
    "description": "Bikaner-based raw wool wholesale supply, specializing in 30+ and 32-34+ micron carpet grade greasy and scoured wool for spinning mills across India.",
    "areaServed": "India",
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Bikaner Raw Wool Sourcing & Supply"
    }
  };

  const schema = [breadcrumbSchema, serviceSchema];

  return (
    <div className="space-y-16 sm:space-y-24 py-6">
      <SEO
        title="Wool Supplier in Bikaner | S.K. Woollens — Mandi Hub Supply Since 1965"
        description="Looking for a reliable wool supplier in Bikaner? S.K. Woollens (Un Mandi B-26) supplies specification-graded raw wool, 30+ & 32-34+ micron carpet fleece in greasy & scoured bales."
        keywords="wool supplier in Bikaner, wool dealers in Bikaner, carpet wool supplier Bikaner, raw wool trading Bikaner, Bikaner wool mandi, wool supplier in Rajasthan"
        canonicalUrl="https://skwoolens.trivanahospitality.com/en/wool-supplier-bikaner"
        schema={schema}
      />

      {/* 1. Header Banner */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-gradient-to-br from-wool-sand/50 via-white to-wool-sand/30 border border-wool-border rounded-3xl p-8 sm:p-14 shadow-subtle">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-7 space-y-4">
              <div className="wool-badge inline-flex">
                <MapPin className="w-3.5 h-3.5 text-wool-bronze" />
                <span>Bikaner Mandi Epicentre | Un Mandi B-26</span>
              </div>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-wool-charcoal tracking-tight font-display">
                Wool Supplier in Bikaner, Rajasthan
              </h1>
              <p className="text-base sm:text-lg text-wool-slate leading-relaxed">
                Headquartered inside the historic Un Mandi of Bikaner, S.K. Woollens supplies specification-graded raw fleece, carpet-grade wool, and bulk commercial lots to yarn spinners and industrial mills across India.
              </p>

              <div className="p-4 bg-white/80 border border-wool-border rounded-xl text-xs sm:text-sm text-wool-slate">
                <span className="font-semibold text-wool-bronze block mb-1">
                  Established 1965 Trade Heritage:
                </span>
                Continuing the long-standing mandi trading legacy of Bulaki Chand Kesari Chand, combining multi-generational grading mastery with modern B2B supply predictability.
              </div>

              <div className="pt-2 flex flex-wrap items-center gap-3">
                <a
                  href="https://wa.me/917014581250?text=Hi%2C%20I%20am%20looking%20for%20a%20reliable%20wool%20supplier%20in%20Bikaner"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary inline-flex items-center gap-2"
                >
                  <MessageCircle className="w-4 h-4 text-emerald-300" />
                  <span>Enquire for Bikaner Wool Supply</span>
                  <ArrowRight className={`w-4 h-4 ${isRTL ? "rotate-180" : ""}`} />
                </a>

                <a href="tel:+917014581250" className="btn-secondary flex items-center gap-2">
                  <Phone className="w-4 h-4 text-wool-bronze" />
                  <span>Call +91 7014581250</span>
                </a>
              </div>
            </div>

            <div className="lg:col-span-5 relative">
              <div className="relative rounded-2xl overflow-hidden shadow-xl border border-wool-border bg-white">
                <img
                  src={mandiImg}
                  alt="Bikaner Un Mandi Wool Trading Hub - S.K. Woollens"
                  className="w-full h-72 sm:h-80 object-cover"
                  loading="eager"
                />
                <div className="p-3 bg-wool-sand/80 border-t border-wool-border text-center text-xs font-semibold text-wool-charcoal">
                  Direct Mandi Presence: B-26, Un Mandi, Antyodaya Nagar, Bikaner
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 2. Why Bikaner is India's Wool Capital */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white border border-wool-border rounded-3xl p-6 sm:p-10 shadow-subtle space-y-6">
          <div className="max-w-3xl space-y-3">
            <div className="wool-badge">
              <Award className="w-3.5 h-3.5 text-wool-bronze" />
              <span>Strategic Sourcing Hub</span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-wool-charcoal font-display">
              Why Sourcing from a Bikaner Wool Supplier Matters
            </h2>
            <p className="text-sm sm:text-base text-wool-slate leading-relaxed">
              Bikaner, Rajasthan is internationally recognized as the largest raw wool trading hub in Asia. The arid regional climate and indigenous sheep breeds produce raw fleece celebrated for high bulk, crisp springiness, and superior tensile durability — the defining characteristics needed for premium carpet yarns and heavy industrial woolen fabrics.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-4">
            <div className="wool-card p-6 space-y-3 bg-wool-sand/20">
              <Building2 className="w-6 h-6 text-wool-bronze" />
              <h3 className="text-base font-bold text-wool-charcoal">Direct Mandi Gate Procurement</h3>
              <p className="text-xs text-wool-slate leading-relaxed">
                Operating directly at B-26 Un Mandi allows us to inspect, sort, and select newly arrived clips directly from breeders and local mandis before they enter wide commercial circulation.
              </p>
            </div>

            <div className="wool-card p-6 space-y-3 bg-wool-sand/20">
              <Sliders className="w-6 h-6 text-wool-bronze" />
              <h3 className="text-base font-bold text-wool-charcoal">30+ & 32–34+ Micron Specialization</h3>
              <p className="text-xs text-wool-slate leading-relaxed">
                We specialize in coarse carpet-grade wools specifically graded for spinning mills requiring high crimp, minimum vegetable matter (VM), and predictable carding yield.
              </p>
            </div>

            <div className="wool-card p-6 space-y-3 bg-wool-sand/20">
              <PackageCheck className="w-6 h-6 text-wool-bronze" />
              <h3 className="text-base font-bold text-wool-charcoal">Warehouse Storage & Lot Stability</h3>
              <p className="text-xs text-wool-slate leading-relaxed">
                Our spacious Bikaner godowns allow us to store pressed bales securely, ensuring consistent lot uniformity for spinning mills across changing harvest seasons.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 3. Wool Specifications Available in Bikaner */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto space-y-3 mb-10">
          <div className="wool-badge">
            <Sliders className="w-3.5 h-3.5 text-wool-bronze" />
            <span>Fibre Characteristics</span>
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold text-wool-charcoal font-display">
            Wool Specifications Sourced & Graded in Bikaner
          </h2>
          <p className="text-sm sm:text-base text-wool-muted">
            We inspect every lot hands-on against commercial spinning parameters to eliminate batch variance.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          <div className="bg-white border border-wool-border rounded-2xl p-5 shadow-sm space-y-2">
            <span className="text-[11px] font-bold text-wool-bronze uppercase">Fibre Diameter</span>
            <h4 className="text-base font-bold text-wool-charcoal">30+ & 32–34+ Micron</h4>
            <p className="text-xs text-wool-muted">
              Ideal coarse fibre thickness delivering maximum pile spring-back and wear resistance for hand-knotted and tufted carpets.
            </p>
          </div>

          <div className="bg-white border border-wool-border rounded-2xl p-5 shadow-sm space-y-2">
            <span className="text-[11px] font-bold text-wool-bronze uppercase">Staple Length</span>
            <h4 className="text-base font-bold text-wool-charcoal">50mm – 100mm+</h4>
            <p className="text-xs text-wool-muted">
              Balanced staple lengths optimized for woolen carding lines, minimizing fibre breakage and noil generation during spinning.
            </p>
          </div>

          <div className="bg-white border border-wool-border rounded-2xl p-5 shadow-sm space-y-2">
            <span className="text-[11px] font-bold text-wool-bronze uppercase">Supply Condition</span>
            <h4 className="text-base font-bold text-wool-charcoal">Greasy & Scoured Bales</h4>
            <p className="text-xs text-wool-muted">
              Available as raw unwashed greasy fleece in pressed bales or pre-scoured lots ready for immediate carding.
            </p>
          </div>

          <div className="bg-white border border-wool-border rounded-2xl p-5 shadow-sm space-y-2">
            <span className="text-[11px] font-bold text-wool-bronze uppercase">Natural Shades</span>
            <h4 className="text-base font-bold text-wool-charcoal">White, Cream & Yellow</h4>
            <p className="text-xs text-wool-muted">
              Carefully graded for colour consistency to match specific dye formulas or natural undyed carpet requirements.
            </p>
          </div>
        </div>
      </section>

      {/* 4. Warehouse & Operations Showcase */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center bg-wool-charcoal text-white rounded-3xl p-6 sm:p-12 shadow-2xl">
          <div className="lg:col-span-6 space-y-4">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-white/10 text-wool-sand text-xs font-bold uppercase tracking-wider rounded-full">
              <Scale className="w-3.5 h-3.5 text-wool-sand" />
              <span>Bikaner Godown Facility</span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight font-display">
              Commercial Storage, Weighing & Dispatch Logistics
            </h2>
            <p className="text-xs sm:text-sm text-wool-sand/80 leading-relaxed">
              We operate verified warehouse storage at Bikaner with dedicated weighing bays, hydraulic bale presses, and strict moisture monitoring. Whether you need a trial 5-tonne consignment or a recurring 50-tonne monthly mill contract, we coordinate seamless transport directly to your mill gate.
            </p>
            <ul className="space-y-2 text-xs text-wool-sand/90 pt-2">
              <li className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                <span>Certified weighbridge verification on every consignment</span>
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                <span>Proper moisture and vegetable matter assessment</span>
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                <span>Nationwide truckload logistics across Bhadohi, Panipat, Mirzapur & Jaipur</span>
              </li>
            </ul>
          </div>

          <div className="lg:col-span-6 relative rounded-2xl overflow-hidden border border-white/20">
            <img
              src={balesStorageImg}
              alt="S.K. Woollens Bikaner Warehouse Storage Bales"
              className="w-full h-72 sm:h-80 object-cover"
              loading="lazy"
            />
            <div className="p-3 bg-black/60 backdrop-blur-md border-t border-white/20 text-center text-xs text-wool-sand">
              Commercial Raw Wool Godown — Bikaner, Rajasthan
            </div>
          </div>
        </div>
      </section>

      {/* 5. Related Pages Navigation */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="border border-wool-border bg-white rounded-3xl p-6 sm:p-10 shadow-subtle space-y-6">
          <h3 className="text-xl font-bold text-wool-charcoal font-display">
            Explore Related Wool Supply Capabilities
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Link
              to={lPath("/carpet-wool-supplier-india")}
              className="p-4 border border-wool-border rounded-xl hover:border-wool-bronze hover:bg-wool-sand/20 transition flex flex-col justify-between"
            >
              <div>
                <h4 className="text-sm font-bold text-wool-charcoal">Carpet Wool Supplier India</h4>
                <p className="text-xs text-wool-muted mt-1">Specialized 30+ & 32-34+ micron carpet grades for yarn spinning.</p>
              </div>
              <span className="text-xs font-semibold text-wool-bronze mt-3 inline-flex items-center gap-1">
                Read more <ArrowRight className="w-3 h-3" />
              </span>
            </Link>

            <Link
              to={lPath("/raw-wool-supplier-india")}
              className="p-4 border border-wool-border rounded-xl hover:border-wool-bronze hover:bg-wool-sand/20 transition flex flex-col justify-between"
            >
              <div>
                <h4 className="text-sm font-bold text-wool-charcoal">Raw Wool Supplier India</h4>
                <p className="text-xs text-wool-muted mt-1">Domestic wool sourcing and nationwide bulk delivery networks.</p>
              </div>
              <span className="text-xs font-semibold text-wool-bronze mt-3 inline-flex items-center gap-1">
                Read more <ArrowRight className="w-3 h-3" />
              </span>
            </Link>

            <Link
              to={lPath("/wool-agent-bikaner")}
              className="p-4 border border-wool-border rounded-xl hover:border-wool-bronze hover:bg-wool-sand/20 transition flex flex-col justify-between"
            >
              <div>
                <h4 className="text-sm font-bold text-wool-charcoal">Wool Agent in Bikaner</h4>
                <p className="text-xs text-wool-muted mt-1">On-the-ground commission representation, lot testing & mandi brokerage.</p>
              </div>
              <span className="text-xs font-semibold text-wool-bronze mt-3 inline-flex items-center gap-1">
                Read more <ArrowRight className="w-3 h-3" />
              </span>
            </Link>

            <Link
              to={lPath("/bulk-wool-supply")}
              className="p-4 border border-wool-border rounded-xl hover:border-wool-bronze hover:bg-wool-sand/20 transition flex flex-col justify-between"
            >
              <div>
                <h4 className="text-sm font-bold text-wool-charcoal">Bulk Wool Supply</h4>
                <p className="text-xs text-wool-muted mt-1">Multi-tonne mill supply contracts and scheduled deliveries.</p>
              </div>
              <span className="text-xs font-semibold text-wool-bronze mt-3 inline-flex items-center gap-1">
                Read more <ArrowRight className="w-3 h-3" />
              </span>
            </Link>
          </div>
        </div>
      </section>

      {/* 6. Direct Contact CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-gradient-to-r from-wool-sand via-wool-sand-dark to-wool-sand border border-wool-border rounded-3xl p-8 sm:p-12 text-center space-y-6 shadow-premium">
          <div className="max-w-2xl mx-auto space-y-2">
            <h3 className="text-2xl sm:text-3xl font-extrabold text-wool-charcoal font-display">
              Connect Directly with S.K. Woollens in Bikaner
            </h3>
            <p className="text-xs sm:text-sm text-wool-slate">
              Speak with partners Sunil Banthia or Nikhil Banthia to discuss lot availability, sample requirements, and current Bikaner mandi rates.
            </p>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-4">
            <a
              href="https://wa.me/917014581250?text=Hi%2C%20I%20would%20like%20to%20connect%20regarding%20wool%20sourcing%20from%20Bikaner"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary inline-flex items-center gap-2"
            >
              <MessageCircle className="w-4 h-4 text-emerald-300" />
              <span>WhatsApp: +91 7014581250</span>
            </a>

            <a
              href="tel:+919829109294"
              className="btn-secondary flex items-center gap-2 bg-white"
            >
              <Phone className="w-4 h-4 text-wool-bronze" />
              <span>Call Sunil Banthia: +91 9829109294</span>
            </a>

            <Link to={lPath("/contact")} className="btn-secondary bg-white">
              <span>View Office & Mandi Location</span>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WoolSupplierBikaner;
